import { useMutation, useQueryClient } from '@tanstack/react-query';

import { cohortQueryKeys } from './queryKeys';

/**
 * Minimal duck-typed socket interface — avoids a runtime import of
 * `socket.io-client` in this package so consumers without a socket dep
 * (mostly tests) can still depend on data-access-cohorts.
 */
export interface RevertCohortSocket {
  on: (event: string, listener: (...args: unknown[]) => void) => void;
  off: (event: string, listener: (...args: unknown[]) => void) => void;
  emit: (event: string, ...args: unknown[]) => void;
}

export interface RevertCohortInput {
  projectId: string;
  cohortId: string;
  socket: RevertCohortSocket | null;
  /** Optional ack timeout in milliseconds; defaults to 30s. */
  timeoutMs?: number;
}

/**
 * Canonical TanStack mutation for reverting staged cohort changes. Revert
 * is a socket-emit operation (no HTTP equivalent) — the mutation wraps the
 * emit + `cohort_reverted` ack into TanStack's mutation lifecycle so
 * callers get the usual `mutate / isLoading / isError` ergonomics.
 *
 * The socket is passed in as an argument (rather than read from a hook
 * inside) so the caller controls socket lifecycle and the mutation stays a
 * pure function of its input.
 *
 * Optimistic updates: callers can clear staged-changes state in Zustand
 * from their submit handler and roll back from `onError`. The cohort
 * profile cache invalidates on success.
 */
export const useRevertCohortMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<{ ok: true }, Error, RevertCohortInput>({
    mutationFn: ({ projectId, cohortId, socket, timeoutMs = 30_000 }) => {
      if (!socket) {
        return Promise.reject(new Error('useRevertCohortMutation: socket is not connected'));
      }

      return new Promise<{ ok: true }>((resolve, reject) => {
        let settled = false;

        const cleanup = () => {
          socket.off('cohort_reverted', onReverted);
          clearTimeout(timer);
        };

        const onReverted = () => {
          if (settled) return;
          settled = true;
          cleanup();
          resolve({ ok: true });
        };

        const timer = setTimeout(() => {
          if (settled) return;
          settled = true;
          cleanup();
          reject(new Error('useRevertCohortMutation: timed out waiting for cohort_reverted'));
        }, timeoutMs);

        socket.on('cohort_reverted', onReverted);
        socket.emit('revert', { data: { projectId, cohortId } });
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cohortQueryKeys.all });
    },
  });
};
